'use client';

import React, { useEffect, useState } from 'react';
import { AlertTriangle, Phone, Clock } from 'lucide-react';
import { createClient } from '@/lib/supabase/client';

interface OverdueLoan {
  id: string;
  borrower: string;
  phone: string;
  daysOverdue: number;
  overdueBalance: number;
  loanNumber: string;
}

const formatVT = (amount: number) => `VT ${amount.toLocaleString('en-US')}`;

export default function DashboardOverdue() {
  const [overdueLoans, setOverdueLoans] = useState<OverdueLoan[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchOverdue = async () => {
      const supabase = createClient();
      const today = new Date().toISOString().split('T')[0];

      const { data, error } = await supabase
        .from('loans')
        .select('id, loan_number, outstanding_balance, next_due_date, borrowers(name, phone)')
        .eq('status', 'active')
        .lt('next_due_date', today)
        .order('next_due_date', { ascending: true })
        .limit(10);

      if (!error && data) {
        const mapped: OverdueLoan[] = data.map((loan: any) => {
          const dueDate = new Date(loan.next_due_date);
          const daysOverdue = Math.floor((new Date().getTime() - dueDate.getTime()) / 86400000);
          return {
            id: loan.id,
            borrower: loan.borrowers?.name || '—',
            phone: loan.borrowers?.phone || '—',
            daysOverdue,
            overdueBalance: Number(loan.outstanding_balance) || 0,
            loanNumber: loan.loan_number || loan.id,
          };
        });
        setOverdueLoans(mapped);
      } else {
        setOverdueLoans([]);
      }
      setLoading(false);
    };

    fetchOverdue();
  }, []);

  if (loading) {
    return (
      <div className="card-elevated h-full flex items-center justify-center">
        <p className="text-sm text-muted-foreground">Loading overdue loans...</p>
      </div>
    );
  }

  return (
    <div className="card-elevated h-full">
      <div className="flex items-center justify-between px-5 py-4 border-b border-border">
        <div className="flex items-center gap-2">
          <div className="w-7 h-7 rounded-lg bg-negative-subtle flex items-center justify-center">
            <AlertTriangle size={14} className="text-negative" />
          </div>
          <h2 className="text-sm font-semibold text-foreground">Overdue Loans</h2>
        </div>
        <span className="text-xs font-bold px-2 py-0.5 rounded-full bg-negative-subtle text-negative border border-negative/20">
          {overdueLoans.length} Active
        </span>
      </div>

      {overdueLoans.length === 0 ? (
        <div className="px-5 py-8 text-center">
          <p className="text-sm text-muted-foreground">No overdue loans</p>
        </div>
      ) : (
        <div className="divide-y divide-border">
          {overdueLoans.map((loan) => (
            <div key={loan.id} className="px-5 py-3.5 hover:bg-muted/30 transition-colors duration-150">
              <div className="flex items-start justify-between gap-2 mb-1.5">
                <div className="min-w-0">
                  <p className="text-sm font-semibold text-foreground truncate">{loan.borrower}</p>
                  <p className="text-[10px] font-mono text-muted-foreground">{loan.loanNumber}</p>
                </div>
                <div className="flex items-center gap-1 shrink-0">
                  <Clock size={10} className="text-negative" />
                  <span className="text-xs font-bold text-negative">{loan.daysOverdue}d</span>
                </div>
              </div>
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-1 text-muted-foreground">
                  <Phone size={11} />
                  <span className="text-[11px]">{loan.phone}</span>
                </div>
                <span className="text-xs font-mono font-bold text-warning">
                  {formatVT(loan.overdueBalance)}
                </span>
              </div>
            </div>
          ))}
        </div>
      )}

      {overdueLoans.length > 0 && (
        <div className="px-5 py-3 border-t border-border">
          <div className="flex items-center justify-between text-xs">
            <span className="text-muted-foreground">Total overdue balance</span>
            <span className="font-mono font-bold text-negative">
              {formatVT(overdueLoans.reduce((s, l) => s + l.overdueBalance, 0))}
            </span>
          </div>
        </div>
      )}
    </div>
  );
}